/**
 * CMS data fetchers — fetch from Supabase, fall back to static data if empty/error.
 * All public pages should import from here (or from useCMSData hooks) rather than
 * directly from src/data/*.ts so edits in the admin take effect on the frontend.
 */
import { supabase } from './supabase';
import {
  locations as staticLocations,
  countrySummaries as staticCountries,
  type Location,
} from '../data/locations';
import { caseStudies as staticCaseStudies, type CaseStudy } from '../data/caseStudies';
import {
  serviceCatalog as staticServices,
  type Service,
  type ServicePillar,
} from '../data/services';
import { projects as staticProjects } from '../data/projects';

// ─── LOCATIONS ────────────────────────────────────────────────────────────────

function rowToLocation(row: Record<string, any>): Location {
  const city = row.city ?? '';
  const hint = row.market_hint ?? '';
  return {
    slug: row.slug,
    city,
    region: row.region ?? '',
    country: row.country ?? '',
    countrySlug: row.country_slug ?? '',
    countryCode: row.country_code ?? '',
    continent: row.continent ?? 'Asia',
    currency: (row.currency ?? 'INR') as Location['currency'],
    timezone: row.timezone ?? 'Asia/Kolkata',
    language: row.language ?? 'English',
    phonePrefix: row.phone_prefix ?? '+91',
    keyIndustries: row.key_industries ?? [],
    complianceNotes: row.compliance_notes ?? [],
    marketContext: hint
      ? `${city} has a ${hint}. Businesses here expect measurable outcomes, clear delivery timelines, and partners who understand local buyer behavior.`
      : '',
    businessCulture: `${city} teams typically value fast communication, transparent reporting, and clear accountability. OWL supports this with structured weekly updates and outcome-focused delivery.`,
    localCompetitorLandscape: `${city} has strong local and international digital agencies, so differentiation comes from execution quality, speed, and measurable ROI.`,
    whyOWLForThisMarket: `Headquartered in Delhi, India, OWL combines deep engineering capability with timezone-overlap delivery for ${city} businesses without compromising quality.`,
    populationContext: row.population_hint ?? '',
    localCTAText: `Get a Free Consultation for Your ${city} Business`,
  };
}

export async function fetchLocations(): Promise<Location[]> {
  try {
    const { data, error } = await supabase
      .from('cms_locations')
      .select('*')
      .eq('is_active', true)
      .order('sort_order');
    if (error || !data?.length) return staticLocations;
    return data.map(rowToLocation);
  } catch {
    return staticLocations;
  }
}

export async function fetchLocationBySlug(slug: string): Promise<Location | undefined> {
  try {
    const { data, error } = await supabase
      .from('cms_locations')
      .select('*')
      .eq('slug', slug)
      .eq('is_active', true)
      .maybeSingle();
    if (error || !data) return staticLocations.find((l) => l.slug === slug);
    return rowToLocation(data);
  } catch {
    return staticLocations.find((l) => l.slug === slug);
  }
}

export async function fetchLocationsByCountry(countrySlug: string): Promise<Location[]> {
  try {
    const { data, error } = await supabase
      .from('cms_locations')
      .select('*')
      .eq('country_slug', countrySlug)
      .eq('is_active', true)
      .order('sort_order');
    if (error || !data?.length)
      return staticLocations.filter((l) => l.countrySlug === countrySlug);
    return data.map(rowToLocation);
  } catch {
    return staticLocations.filter((l) => l.countrySlug === countrySlug);
  }
}

// ─── COUNTRIES ────────────────────────────────────────────────────────────────

export async function fetchCountries(): Promise<{ slug: string; name: string }[]> {
  try {
    const { data, error } = await supabase
      .from('cms_countries')
      .select('slug, name, sort_order')
      .order('sort_order');
    if (error || !data?.length) return staticCountries;
    return data;
  } catch {
    return staticCountries;
  }
}

// ─── CASE STUDIES ─────────────────────────────────────────────────────────────

function rowToCaseStudy(row: Record<string, any>): CaseStudy {
  return {
    slug: row.slug,
    industry: row.industry ?? '',
    label: row.label ?? '',
    title: row.title ?? '',
    titleAccent: row.title_accent ?? '',
    subtitle: row.subtitle ?? '',
    summary: row.summary ?? '',
    challenge: row.challenge ?? [],
    solution: row.solution ?? [],
    impact: row.impact ?? [],
    techStack: row.tech_stack ?? [],
    stats: row.stats ?? [],
    locationSlugs: row.location_slugs,
    serviceSlugs: row.service_slugs,
  };
}

export async function fetchCaseStudies(): Promise<CaseStudy[]> {
  try {
    const { data, error } = await supabase
      .from('cms_case_studies')
      .select('*')
      .eq('is_published', true)
      .order('sort_order');
    if (error || !data?.length) return staticCaseStudies;
    return data.map(rowToCaseStudy);
  } catch {
    return staticCaseStudies;
  }
}

export async function fetchCaseStudyBySlug(slug: string): Promise<CaseStudy | undefined> {
  try {
    const { data, error } = await supabase
      .from('cms_case_studies')
      .select('*')
      .eq('slug', slug)
      .eq('is_published', true)
      .maybeSingle();
    if (error || !data) return staticCaseStudies.find((cs) => cs.slug === slug);
    return rowToCaseStudy(data);
  } catch {
    return staticCaseStudies.find((cs) => cs.slug === slug);
  }
}

// ─── SERVICES ─────────────────────────────────────────────────────────────────

const defaultPriceRange = {
  INR: { min: 120000, max: 1500000 },
  USD: { min: 2500, max: 20000 },
  GBP: { min: 1900, max: 16000 },
  AED: { min: 9000, max: 73000 },
  EUR: { min: 2200, max: 18500 },
};

function rowToService(row: Record<string, any>): Service {
  return {
    slug: row.slug,
    name: row.name,
    shortDescription: row.short_description ?? `${row.name} for growth-stage and enterprise businesses.`,
    longDescription: row.long_description ?? '',
    icon: row.icon ?? 'Globe',
    pillar: (row.pillar ?? 'web-digital') as ServicePillar,
    features: row.features ?? [],
    technologies: row.technologies ?? [],
    deliverables: row.deliverables ?? [],
    timelineWeeks: { min: row.timeline_min_weeks ?? 2, max: row.timeline_max_weeks ?? 16 },
    priceRange: row.price_ranges ?? defaultPriceRange,
    relatedServices: row.related_services ?? [],
  };
}

export async function fetchServices(): Promise<Service[]> {
  try {
    const { data, error } = await supabase
      .from('cms_services')
      .select('*')
      .eq('is_active', true)
      .order('sort_order');
    if (error || !data?.length) return staticServices;
    return data.map(rowToService);
  } catch {
    return staticServices;
  }
}

export async function fetchServiceBySlug(slug: string): Promise<Service | undefined> {
  try {
    const { data, error } = await supabase
      .from('cms_services')
      .select('*')
      .eq('slug', slug)
      .eq('is_active', true)
      .maybeSingle();
    if (error || !data) return staticServices.find((s) => s.slug === slug);
    return rowToService(data);
  } catch {
    return staticServices.find((s) => s.slug === slug);
  }
}

// ─── PROJECTS ─────────────────────────────────────────────────────────────────

type Project = (typeof staticProjects)[number];

function rowToProject(row: Record<string, any>): Project {
  return {
    id: row.project_id ?? String(row.id),
    industry: row.industry ?? '',
    name: row.name ?? '',
    metric: row.metric ?? '',
    metricLabel: row.metric_label ?? '',
    year: row.year ?? '',
    slug: row.slug ?? '',
  };
}

export async function fetchProjects(): Promise<Project[]> {
  try {
    const { data, error } = await supabase
      .from('cms_projects')
      .select('*')
      .eq('is_published', true)
      .order('sort_order');
    if (error || !data?.length) return staticProjects;
    return data.map(rowToProject);
  } catch {
    return staticProjects;
  }
}
